/* eslint-disable max-lines */
import { formatISO } from 'date-fns'
import { useCallback, useMemo } from 'react'
import { Schemas } from '~/api/gen'
import { Img } from '~/components/elements/Img'
import { PriceLabel } from '~/components/elements/PriceLabel'
import { usePostAffiliates } from '~/features/shared/affiliate/hooks'
import { setAffiliateObjectCookie } from '~/features/shared/affiliate/lib/setAffiliateObjectCookie'
import { ItemImageLabel } from '~/features/shared/gtmEvent/types/coordinateDetail'
import { GtmEvent } from '~/features/shared/gtmEvent/types/gtmEvent'
import { getRelatedItemAlt } from '~/features/shared/item/lib/getRelatedItemAlt'
import { buildUrl } from '~/features/shared/search/coordinate/builder/buildUrl'
import { buildItemListUrl } from '~/features/shared/search/item/buildItemListUrl'
import { useAccessTokenValue } from '~/hooks/useAccessTokenValue'
import { useBusinessEventLogging } from '~/hooks/useBusinessEventLogging'
import { useLoginMember } from '~/hooks/useLoginMember'
import { getColorFromColorGroupId } from '~/lib/color'
import { NEXT_PUBLIC_ZOZOTOWN_URL } from '~/lib/env'
import { sexToGender } from '~/lib/gender/converter'
import { Gender } from '~/lib/gender/types'
import { getMallType } from '~/lib/getMallType'
import { getUA } from '~/lib/getUA'
import { concatParamsForZozotownUrl } from '~/lib/zozotown/concatParamsForZozotownUrl'
import { KIds } from '~/lib/zozotown/kIds'
import { Mall } from '~/types/mall'
import { PpfSellStatus } from '~/types/product/ppfSellStatus'
import { ScreenIds } from '~/types/screenIds'
import { sendGtmEvItemImage, sendGtmEvItemImageButton } from '../lib/gtmEvent'
import { BuyButton } from './BuyButton'

// 着用アイテムうち、アイテムに紐づいている場合はtrue
const isItemLinked = (coordinateItem: Schemas.coordinate_item_resource) =>
  coordinateItem.item.id !== null

type ButtonType = 'buy' | 'search' | 'itemDetail' | 'itemList'
const getButtonType = (
  purchaseItem: Omit<Schemas.purchase_item, 'zozotown_sale'> | null,
  coordinateItem: Schemas.coordinate_item_resource
): ButtonType =>
  purchaseItem !== null
    ? 'buy' //「ZOZOTOWNで購入する」ボタン、または「このアイテムを購入する」（他のモールで買う）ボタン
    : coordinateItem.item.search_condition_label
      ? 'search' //「ZOZOTOWNで探す」ボタン
      : isItemLinked(coordinateItem)
        ? 'itemDetail' //「アイテム詳細を見る」ボタン（アイテム詳細ページへのリンク）
        : 'itemList' //「似ているアイテムを探す」ボタン（アイテム一覧ページへのリンク）

type ViewProps = {
  item: Schemas.coordinate_item_resource
  hasCoupon: boolean
  purchaseItem: Omit<Schemas.purchase_item, 'zozotown_sale'> | null
  onClickItemImage: () => void
  onClickItemButton: () => void
  itemButtonUrl: string
  gender: Gender
}

/* eslint-disable complexity */
export const UseItemView: React.FC<ViewProps> = ({
  item,
  hasCoupon,
  purchaseItem,
  onClickItemImage,
  onClickItemButton,
  itemButtonUrl,
  gender,
}) => {
  const categoryName = item.item.item_child_category
    ? item.item.item_child_category.name
    : item.item.item_category?.name

  const mallType = getMallType(purchaseItem?.shop)

  const buttonType = getButtonType(purchaseItem, item)

  const itemText = getRelatedItemAlt({
    brandName: item.item.brand.name,
    itemName: item.name,
    categoryName: item.item.item_child_category
      ? item.item.item_child_category.name
      : item.item.item_category?.name,
  })

  return (
    <div className="xl:flex">
      <a
        className="relative flex justify-center border border-gray-300 xl:h-[120px] xl:w-[100px] xl:shrink-0 xl:overflow-hidden xl:rounded-[3px] xl:border-none xl:hover:opacity-70"
        href={
          isItemLinked(item)
            ? `/item/${item.item.id}/`
            : `/snapitem/${item.id}/`
        }
        onClick={onClickItemImage}
      >
        <Img src={item.image.url_215} width={215} height={258} alt={itemText} />
        <div className="absolute bottom-[6px] left-[6px] flex flex-col gap-[2px] empty:hidden xl:bottom-[5px] xl:left-[5px] xl:gap-[1px]">
          {hasCoupon && (
            <span className="flex h-[17px] items-center justify-center rounded-[2px] bg-orange-500 px-[6px] text-[10px] leading-none text-white">
              クーポン対象
            </span>
          )}
          {item.item.display_price && (
            <p className="leading-none">
              <PriceLabel displayPrice={item.item.display_price} />
            </p>
          )}
        </div>
      </a>
      <div className="min-w-0 xl:grow xl:pl-[18px] xl:pr-[23px]">
        <p className="truncate pt-1 text-[10px] leading-[1.4] xl:mt-[-3px] xl:pt-0 xl:text-[15px] xl:font-bold xl:leading-[1.6]">
          <span className="hidden xl:inline">
            {item.item.brand.key !== null &&
            item.item.brand.name !== null &&
            item.item.brand.id !== null ? (
              <a
                href={buildUrl({
                  snapItems: [
                    {
                      brand: {
                        id: item.item.brand.id,
                        key: item.item.brand.key,
                        name: item.item.brand.name,
                      },
                    },
                  ],
                })}
                className="xl:hover:underline"
              >
                {item.item.brand.name}
              </a>
            ) : (
              item.item.brand.name
            )}
          </span>
          <span className="xl:hidden">{item.item.brand.name}</span>
        </p>
        {!isItemLinked(item) && item.name && (
          <p className="hidden xl:block xl:truncate xl:pt-[5px] xl:text-[12px] xl:leading-none">
            {item.name}
          </p>
        )}
        <p className="truncate text-[10px] leading-[1.4] xl:pt-[3px] xl:text-[12px]">
          <a
            href={buildUrl({
              snapItems: [
                {
                  category: item.item.item_category?.id
                    ? {
                        name: item.item.item_category.name,
                        key: item.item.item_category.keyword,
                        id: item.item.item_category.id,
                        child:
                          item.item.item_child_category?.id &&
                          item.item.item_child_category?.keyword
                            ? {
                                name: item.item.item_child_category.name,
                                key: item.item.item_child_category.keyword,
                                id: item.item.item_child_category.id,
                              }
                            : undefined,
                      }
                    : undefined,
                  colorGroupId: item.color_group?.id ?? undefined,
                },
              ],
            })}
            className="hidden xl:inline xl:text-blue-500 xl:hover:underline"
          >
            {categoryName}
            {item.color_group?.id &&
              ` (${getColorFromColorGroupId(item.color_group.id)})`}
          </a>
          <span className="xl:hidden">{categoryName}</span>
        </p>
        {isItemLinked(item) && item.size && (
          <p className="truncate pt-[2px] text-[10px] leading-none xl:pt-[5px] xl:text-[12px]">
            サイズ：{item.size}
          </p>
        )}
        {isItemLinked(item) &&
          item.item.item_aggregate &&
          item.item.item_aggregate.coordinate_count > 1 && (
            <a
              href={`/coordinate/?item_id=${item.item.id}`}
              className="mt-[1px] block pb-[2px] pt-1 text-[11px] leading-[1.8] text-blue-500 xl:text-[12px] xl:hover:underline"
            >
              着用コーデ：{item.item.item_aggregate.coordinate_count}件
            </a>
          )}
        <div className="flex flex-col items-center space-y-2 pb-5 pt-[10px] xl:items-start xl:pb-0 xl:pt-1">
          {buttonType === 'buy' && (
            <BuyButton
              href={itemButtonUrl}
              aria-label={`${itemText}をZOZOTOWNで購入する`}
              onClick={onClickItemButton}
              target="_blank"
              rel="noreferrer"
            >
              <span className="xl:hidden">商品を見る</span>
              <span className="hidden xl:inline">
                {mallType === Mall.Zozotown
                  ? 'ZOZOTOWNで購入する'
                  : 'このアイテムを購入する'}
              </span>
            </BuyButton>
          )}
          {buttonType === 'search' && (
            <BuyButton
              color="outlined"
              href={itemButtonUrl}
              aria-label={`${itemText}をZOZOTOWNで探す`}
              onClick={onClickItemButton}
              target="_blank"
              rel="noreferrer"
            >
              <span className="xl:hidden">探す</span>
              <span className="hidden xl:inline xl:text-blue-500">
                ZOZOTOWNで探す
              </span>
            </BuyButton>
          )}
          {buttonType === 'itemDetail' && (
            <div className="hidden xl:block">
              <BuyButton
                color="outlined"
                href={`/item/${item.item.id}`}
                onClick={onClickItemButton}
              >
                <span className="xl:text-blue-500">アイテム詳細を見る</span>
              </BuyButton>
            </div>
          )}
          {buttonType === 'itemList' && (
            <div className="hidden xl:block">
              <BuyButton
                color="outlined"
                href={buildItemListUrl({
                  brand: item.item.brand.id
                    ? { id: item.item.brand.id }
                    : undefined,
                  category: item.item.item_category?.id
                    ? {
                        id: item.item.item_category.id,
                        child: item.item.item_child_category?.id
                          ? { id: item.item.item_child_category.id }
                          : undefined,
                      }
                    : undefined,
                  colorGroupId: item.color_group?.id ?? undefined,
                  gender,
                })}
              >
                <span className="xl:text-blue-500">似ているアイテムを探す</span>
              </BuyButton>
            </div>
          )}
          {item.product?.ppf_sell_status === PpfSellStatus.isOpen && (
            <div className="xl:hidden">
              <BuyButton
                href={`/products/${item.product.key}/`}
                color="secondary"
              >
                フリマで見る
              </BuyButton>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

type Props = {
  coordinateItem: Schemas.coordinate_item_resource
  coordinate: Schemas.coordinate_large_with_member_shop_brands
}

export const UseItem: React.FC<Props> = ({ coordinateItem, coordinate }) => {
  const { uuid, sendLog } = useBusinessEventLogging()
  const accessToken = useAccessTokenValue()
  const { data: member } = useLoginMember(accessToken)
  const { postAffiliates } = usePostAffiliates()

  // クーポンを持っているか
  const hasCoupon = useMemo(
    () => coordinateItem.item.purchase_items.some((i) => i.coupon),
    [coordinateItem]
  )

  const purchaseItem = coordinateItem.item.purchase_items[0] ?? null

  const mallType: Mall = getMallType(purchaseItem?.shop)

  /**
   * kid 販売しているアイテムは3億+コーデID・それ以外は4億+アイテムID
   */
  const kid =
    (purchaseItem ? KIds.BuyByCoordinate : KIds.SearchByCoordinate) +
    coordinate.id

  /**
   * utmCampaign
   */
  const utmCampaign = purchaseItem
    ? `brandname${coordinateItem.item.brand.name}_itemID${
        coordinateItem.item.id
      }_kid${kid}_coupon${hasCoupon ? 1 : 0}`
    : `relsnap_${coordinate.id}`

  /**
   * 探す・商品を見るボタンのURL
   */
  const itemButtonUrl = useMemo(() => {
    const baseUrl =
      purchaseItem?.shop?.mall_url ??
      coordinateItem.item.search_condition_url ??
      NEXT_PUBLIC_ZOZOTOWN_URL

    // 他社ECはパラメータなし
    if (mallType === Mall.Other) return baseUrl

    // ZOZOTOWNはパラメータ追加
    return concatParamsForZozotownUrl(baseUrl, {
      kid,
      utmCampaign,
      uuid,
    })
  }, [
    coordinateItem.item.search_condition_url,
    kid,
    mallType,
    purchaseItem,
    utmCampaign,
    uuid,
  ])

  const onClickItemImage = useCallback(() => {
    // GAの処理
    const eventLabel = (): ItemImageLabel => {
      switch (mallType) {
        case Mall.Zozotown:
          return `snapID${coordinate.id}_itemID${
            coordinateItem.item.id ?? 0
          }_coupon${hasCoupon ? 1 : 0}`
        case Mall.Other:
          return `snapID${coordinate.id}_itemID${coordinateItem.item.id ?? 0}`
        case Mall.None:
          return `snapID${coordinate.id}_snapitemID${coordinateItem.id}`
      }
    }
    const linkSiteType = () => {
      switch (mallType) {
        case Mall.Zozotown:
          return 'zozo_item_detail'
        case Mall.Other:
          return 'other_item_detail'
        case Mall.None:
          return 'zozo_item_search'
      }
    }
    sendGtmEvItemImage(eventLabel(), linkSiteType())

    // アフィリエイトの処理
    // PCでなおかつ自作アイテムの時は送信しない
    if (getUA().isPC && !isItemLinked(coordinateItem)) return
    setAffiliateObjectCookie({
      objectId: coordinate.id,
      screenId: ScreenIds.CoordinateDetail,
    })
  }, [coordinateItem, coordinate.id, mallType, hasCoupon])

  const buttonType: ButtonType = getButtonType(purchaseItem, coordinateItem)
  const onClickItemButton = useCallback(() => {
    const itemButtonEventData = (timestamp: string): GtmEvent => {
      const url = new URL(location.href)
      url.searchParams.set('vir', url.search === '' ? 'buyclick' : 'buy')
      const trackPageview = `${url.pathname}${url.search}`

      const event = () => {
        if (purchaseItem) return 'item_buybtn'
        return coordinateItem.item.id
          ? 'item_searchzozobtn'
          : 'item_searchzozobtn_org'
      }

      const eventCategory = purchaseItem ? 'buy' : 'buy_szozo'

      const linkSiteType = () => {
        switch (mallType) {
          case Mall.Zozotown:
            return 'zozo_item_detail'
          case Mall.Other:
            return 'other_item_detail'
          case Mall.None:
            return 'zozo_item_search'
        }
      }

      const itemId = coordinateItem.item.id
        ? { itemId: coordinateItem.item.id }
        : { snapitemid: coordinateItem.id }

      const eventData: GtmEvent = {
        event: event(),
        eventCategory: eventCategory,
        link_site_type: linkSiteType(),
        ...itemId,
        brandName: coordinateItem.item.brand.name ?? '',
        href: itemButtonUrl,
        trackPageview: trackPageview,
        title: document.title,
        custom_client_timestamp: timestamp,
      }

      return purchaseItem
        ? { ...eventData, kid: kid, shopcoupon: hasCoupon ? 1 : 0 }
        : eventData
    }

    postAffiliates({
      before_screen_id: Number(ScreenIds.CoordinateDetail),
      content_id: coordinate.id,
      item_id: coordinateItem.item.id ? coordinateItem.item.id : undefined,
      // item_idがない場合（自作アイテム）のみ、coordinate_item_idを送信
      // ref. https://docs.google.com/spreadsheets/d/1eWJGl92woVbpvIgCDD92Mk_DK97JdHlx35k9MWAZLXA/edit?gid=669605726#gid=669605726&range=K7:K9
      coordinate_item_id: coordinateItem.item.id
        ? undefined
        : coordinateItem.id,
      buy_item_id: purchaseItem?.purchase_item_id,
      action_type: purchaseItem ? 'buy' : 'search',
    })

    // PCかつ「アイテム詳細を見る」ボタンの場合GTMEvent、ビジネスイベントは送信しない。
    if (getUA().isPC && buttonType === 'itemDetail') return

    // GAの処理
    const timestamp = formatISO(new Date())
    sendGtmEvItemImageButton(itemButtonEventData(timestamp))

    // 他社ECはビジネスイベントロギングを送信しない
    if (mallType === Mall.Other) return

    sendLog({
      loggedInMemberId: member ? member.id : null,
      timestamp: timestamp,
      coordinateId: coordinate.id,
      coordinateMemberId: coordinate.member.id,
      coordinateMemberShopId: coordinate.member.shop?.id
        ? coordinate.member.shop.id
        : null,
      coordinateItemId: coordinateItem.item.id ? null : coordinateItem.id,
      itemId: coordinateItem.item.id ? coordinateItem.item.id : null,
    })
  }, [
    member,
    coordinate.id,
    coordinate.member.id,
    coordinate.member.shop?.id,
    coordinateItem.item.id,
    coordinateItem.item.brand.name,
    coordinateItem.id,
    purchaseItem,
    buttonType,
    mallType,
    sendLog,
    itemButtonUrl,
    kid,
    hasCoupon,
    postAffiliates,
  ])

  return (
    <UseItemView
      item={coordinateItem}
      hasCoupon={hasCoupon}
      purchaseItem={purchaseItem}
      onClickItemImage={onClickItemImage}
      onClickItemButton={onClickItemButton}
      itemButtonUrl={itemButtonUrl}
      gender={sexToGender(coordinate.sex_id)}
    />
  )
}
